import { Inject, Injectable, isDevMode } from '@angular/core';
import { LocalStorage } from '@lockton-apps/shared/data-access/storage';
import {
  ApplicationConfig,
  Proposal,
  Quote,
} from '@lockton-apps/shared/interfaces';
import {
  defaultsDeep,
  filter,
  forEach,
  get,
  includes,
  keys,
  uniqueId,
} from 'lodash';
import { map, Observable, of } from 'rxjs';
import { PROPOSALS_MOCKS } from '@lockton-apps/shared/util/base-path';
import { getDevConfig } from '@lockton-apps/shared/util/helpers';

@Injectable({
  providedIn: 'root',
})
export class ProposalMocksService {
  private cachedConfig!: ApplicationConfig;

  get isUsingMocks(): boolean {
    return this.cachedConfig && getDevConfig('proposalForm.runLocally');
  }

  constructor(@Inject(PROPOSALS_MOCKS) private proposalsMocks: unknown[]) {}

  createMockedQuote(
    propFormId: string,
    portalId: number,
    configId: number,
    clientId: number
  ): Quote {
    return {
      id: uniqueId(),
      portalId,
      propFormId,
      clientId,
      proposal: this.getLocalProposal(portalId, configId, clientId, propFormId),
    } as unknown as Quote;
  }

  setConfig(config: ApplicationConfig): void {
    this.cachedConfig = config;
  }

  saveProposalLocally(proposal: Proposal): Observable<Proposal> {
    LocalStorage.set(
      `proposal.${proposal.portalId}.${proposal.subType || '1'}.${
        proposal.clientId
      }.${proposal.id}_v${this.cachedConfig.version}`,
      proposal
    );

    return of(proposal);
  }

  submitProposalLocally(proposal: Proposal): Observable<Quote[]> {
    return this.saveProposalLocally(proposal).pipe(
      map(() => [{ type: 'new-business' } as Quote])
    );
  }

  getLocalProposal(
    portalId: number,
    configId: number,
    clientId: number,
    proposalId: string
  ): Proposal {
    let proposal: unknown = LocalStorage.get(
      `proposal.${portalId}.${configId}.${clientId}.${proposalId}_v${this.cachedConfig.version}`
    );

    if (!proposal) {
      this.clearStoredProposals(portalId, clientId, proposalId);

      proposal = defaultsDeep(
        get(
          this.proposalsMocks,
          [portalId, configId, clientId],
          get(this.proposalsMocks, [portalId, configId, 'DEFAULT'])
        ),
        {
          id: proposalId,
          portalId,
          clientId,
          canEdit: true,
          createdDate: new Date().toISOString(),
          formData: {
            data: {},
          },
        }
      );
    }

    return proposal as Proposal;
  }

  private clearStoredProposals(
    portalId: number,
    clientId: number,
    proposalId: string
  ): void {
    const outOfDateProposals = filter(keys(window.localStorage), (key) =>
      includes(key, `proposal.${portalId}.${clientId}.${proposalId}`)
    );
    if (!outOfDateProposals.length) {
      return;
    }

    if (isDevMode()) {
      console.warn('New version detected: Clearing saved proposals');
    }

    forEach(outOfDateProposals, (proposalKey) => {
      LocalStorage.delete(proposalKey);
    });
  }
}
